import React from 'react'
import { useTranslation } from 'react-i18next'
import Head, { PageHead } from '@fs/zion-head'
import { isTranslationAvailable } from '@fs/zion-locale'
import { useLocation } from '@fs/zion-router'
import { rootstechSocialShare } from '../../images'

const SeoPageHead = ({ seo }) => {
  const [t] = useTranslation()
  const { pathname } = useLocation()
  // NOTE: This does not handle all page titles. In some cases, the title needs specific data we don't have here.
  // SeriesPage, SessionPage, BoothPage, and SpeakerPage set their own title.
  const titleMap = {
    '/': isTranslationAvailable('rootstech.home.page-title')
      ? t('rootstech.home.page-title', 'RootsTech Genealogy Conference & Learning Library')
      : t('navigation.home.label', 'Home'),
    '/search': t('navigation.on-demand.label', 'On Demand'),
    '/library': t('library.header', 'Video Library'),
    '/schedule/': t('navigation.schedule.label', 'Schedule'),
    '/playlist': t('navigation.playlists.label', 'My Playlists'),
    '/playlists/': t('navigation.playlists.label', 'My Playlists'),
    '/history': t('navigation.watch-history.label', 'Watch History'),
    '/dashboard/': t('navigation.dashboard.label', 'Dashboard'),
    '/dashboard/access': t('navigation.dashboard-access.label', 'Dashboard Access'),
    '/dashboard/authorization': t('navigation.dashboard-authorization.label', 'Dashboard Authorization'),
    '/user': t('navigation.user.label', 'User'),
    '/faq': t('navigation.faq.label', 'Frequently Asked Questions'),
    '/about': t('about.banner.heading', 'About Us'),

    '/subscription': t('conference-announce.subscribe.for.updates', 'Subscribe for Updates'),
    '/getting-started': t('search.getting-started.label', 'Getting Started'),

    '/registration/': t('event.reg.attend', 'Register for RootsTech'),
    '/registration/in-person/': t('event.reg.attend.in-person.register', 'In-Person Event Registration'),
    '/registration/online/': t('event.reg.attend.virtual.title', 'Online Event Registration'),
    '/registration/in-person/expohall/': t('event.reg.attend.expohall.register', 'Expo Hall Only Registration'),
    '/registration/in-person/discovery-day/': t(
      'event.reg.attend.discovery-day.register',
      'Discovery Day Registration'
    ),
    '/registration/online/arc/': t(
      'event.reg.attend.arc.register',
      'Archivist and Record Custodian Symposium Online Registration'
    ),
  }

  const clientTitle = titleMap[pathname] ?? t('site-title', 'RootsTech')

  if (!seo) {
    return null
  }
  const { twitter, title, description, openGraph } = seo || {}
  const seoTitle = pathname !== '/' ? title : clientTitle
  const seoDescription =
    pathname !== '/'
      ? description
      : t(
          'rootstech.home.page-description',
          'Explore RootsTech, the world’s largest family history conference. Join inspiring live events and access on-demand genealogy classes anytime, anywhere.'
        )
  const twitterUsername = twitter?.username !== '@FamilySearch' ? twitter?.username : '@RootsTechConf'
  const twitterImage = twitter?.image || {}
  const openGraphTitle = openGraph?.title || seoTitle
  const openGraphDescription = openGraph?.description || seoDescription
  const openGraphImage = openGraph?.image || {}
  const openGraphImageMetadata = openGraphImage?.metadata || {}
  const image = openGraphImage?.url || rootstechSocialShare

  return (
    <>
      <PageHead
        title={seoTitle}
        description={seoDescription}
        socialShareTitle={openGraphTitle}
        socialShareDescription={openGraphDescription}
        siteName={t('global.rootstech', 'RootsTech')}
        image={{
          alt: openGraphTitle,
          url: image,
          width: openGraphImageMetadata?.origWidthPixels || 1200, // 1200
          height: openGraphImageMetadata?.origHeightPixels || 630, // 630
        }}
        twitterSite={twitterUsername}
        twitterCreator={twitterUsername}
        twitterCard="summary_large_image"
        omitSiteName
      />
      <Head>
        {seo?.noIndexRobot && <meta name="robots" content="noindex" />}
        {seo?.noFollowRobot && <meta name="robots" content="nofollow" />}
        {seo?.noImageIndexRobot && <meta name="robots" content="noimageindex" />}
        {image && <meta property="og:image:type" content={getImageType(image)} />}
        {!twitterImage?.url && <meta name="twitter:image" content={image} />}
      </Head>
    </>
  )
}

function getImageType(imageUrl = '') {
  let imageType = 'image/jpeg'
  if (imageUrl.match(/\.png/gi)) imageType = 'image/png'
  if (imageUrl.match(/\.gif/gi)) imageType = 'image/gif'
  if (imageUrl.match(/\.svg/gi)) imageType = 'image/svg+xml'
  if (imageUrl.match(/\.webp/gi)) imageType = 'image/webp'
  return imageType
}

export default SeoPageHead
